import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useCart } from "../context/CartContext";
import Toast from "../components/Toast";

const SearchWrapper = styled.div`
  min-height: 100vh;
  background: var(--background-light);
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SearchSection = styled.section`
  padding: 60px 0;
`;

const SearchBar = styled.div`
  background: white;
  border-radius: 50px;
  padding: 10px;
  display: flex;
  align-items: center;
  max-width: 800px;
  margin: 0 auto 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  input {
    flex: 1;
    border: none;
    outline: none;
    padding: 15px 20px;
    font-size: 1.1rem;
    background: transparent;

    &::placeholder {
      color: #9ca3af;
    }
  }

  button {
    background: var(--secondary-color);
    color: white;
    border: none;
    border-radius: 50px;
    padding: 15px 35px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #c9a660;
      transform: scale(1.05);
    }
  }
`;

const FilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
  margin-bottom: 40px;
`;

const FilterTag = styled.button`
  padding: 10px 20px;
  border: 2px solid var(--secondary-color);
  background: ${(props) =>
    props.active ? "var(--secondary-color)" : "transparent"};
  color: ${(props) => (props.active ? "white" : "var(--secondary-color)")};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--secondary-color);
    color: white;
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  h2 {
    color: var(--text-dark);
    font-size: 1.8rem;
    font-weight: 700;
  }

  .results-count {
    color: var(--text-light);
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImage = styled.div`
  height: 250px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductContent = styled.div`
  padding: 30px;

  h3 {
    font-size: 1.25rem;
    color: var(--text-dark);
    margin-bottom: 10px;
    font-weight: 700;
  }

  .category {
    display: inline-block;
    background: var(--background-light);
    color: var(--secondary-color);
    padding: 5px 15px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 15px;
    text-transform: capitalize;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 20px;
  }

  .features {
    list-style: none;
    padding: 0;
    margin-bottom: 20px;

    li {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      font-size: 0.9rem;
      color: var(--text-light);

      .check {
        color: var(--secondary-color);
        font-weight: bold;
      }
    }
  }
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 10px;
`;

const AddToCartButton = styled.button`
  padding: 12px;
  background: ${(props) => (props.inCart ? "#10b981" : "var(--primary-color)")};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.inCart ? "#059669" : "#0c3a63")};
    transform: translateY(-2px);
  }
`;

const ViewDetailsButton = styled.button`
  padding: 12px;
  background: transparent;
  color: var(--secondary-color);
  border: 2px solid var(--secondary-color);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--secondary-color);
    color: white;
    transform: translateY(-2px);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 80px 20px;

  .icon {
    font-size: 5rem;
    color: #e5e7eb;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 1.8rem;
    color: var(--text-dark);
    margin-bottom: 15px;
  }

  p {
    color: var(--text-light);
    font-size: 1.1rem;
    margin-bottom: 30px;
  }
`;

const SuggestionButton = styled.button`
  background: var(--secondary-color);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }
`;

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [activeCategory, setActiveCategory] = useState("all");
  const [filteredContainers, setFilteredContainers] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();

  // All containers data
  const allContainers = [
    {
      id: 1,
      title: "10FT Shipping Container",
      category: "new",
      image: "/assets/2025/08/10ft-shipping-container-300x300.jpg",
      description: "Perfect for small storage needs and tight spaces.",
      features: [
        "Dimensions: 10' x 8' x 8.5'",
        "Wind and watertight",
        "Steel construction",
        "Cargo doors",
      ],
      fullDescription:
        "Our 10ft shipping containers are perfect for residential and commercial storage needs. Built with high-quality steel construction, these containers are wind and watertight, ensuring your items stay protected from the elements.",
      specifications: {
        "External Length": "10'",
        "External Width": "8'",
        "External Height": "8.5'",
        "Internal Length": "9'4\"",
        "Internal Width": "7'8\"",
        "Internal Height": "7'10\"",
        "Door Opening Width": "7'8\"",
        "Door Opening Height": "7'6\"",
        Weight: "2,850 lbs",
        Capacity: "560 cu ft",
      },
    },
    {
      id: 2,
      title: "20FT Standard Container",
      category: "new",
      image: "/assets/2025/08/20ft-standard-container-300x300.jpg",
      description: "Most popular size for general storage and shipping.",
      features: [
        "Dimensions: 20' x 8' x 8.5'",
        "CSC certified",
        "One-trip containers",
        "Lockable doors",
      ],
      fullDescription:
        "The 20ft standard container is the most versatile and widely used shipping container size. Perfect for both storage and shipping applications, these containers are built to international standards.",
      specifications: {
        "External Length": "20'",
        "External Width": "8'",
        "External Height": "8.5'",
        "Internal Length": "19'4\"",
        "Internal Width": "7'8\"",
        "Internal Height": "7'10\"",
        "Door Opening Width": "7'8\"",
        "Door Opening Height": "7'6\"",
        Weight: "5,070 lbs",
        Capacity: "1,172 cu ft",
      },
    },
    {
      id: 3,
      title: "40FT High Cube",
      category: "new",
      image: "/assets/2025/08/40ft-high-cube-300x300.jpg",
      description: "Extra height for larger items and maximum storage.",
      features: [
        "Dimensions: 40' x 8' x 9.5'",
        "Extra foot of height",
        "Premium quality steel",
        "Double door access",
      ],
      fullDescription:
        "The 40ft High Cube container offers an additional foot of height compared to standard containers, making it ideal for storing taller items or maximizing storage capacity.",
      specifications: {
        "External Length": "40'",
        "External Width": "8'",
        "External Height": "9.5'",
        "Internal Length": "39'5\"",
        "Internal Width": "7'8\"",
        "Internal Height": "8'10\"",
        "Door Opening Width": "7'8\"",
        "Door Opening Height": "8'5\"",
        Weight: "8,775 lbs",
        Capacity: "2,694 cu ft",
      },
    },
    {
      id: 4,
      title: "20FT Used Container",
      category: "used",
      image: "/assets/2025/08/20ft-used-container-300x300.jpg",
      description: "Cost-effective solution with reliable quality.",
      features: [
        "Cargo worthy condition",
        "Structurally sound",
        "Weather resistant",
        "Budget-friendly",
      ],
      fullDescription:
        "Our used containers offer excellent value while maintaining structural integrity and weather resistance. Perfect for budget-conscious customers who need reliable storage.",
      specifications: {
        "External Length": "20'",
        "External Width": "8'",
        "External Height": "8.5'",
        "Internal Length": "19'4\"",
        "Internal Width": "7'8\"",
        "Internal Height": "7'10\"",
        Condition: "Cargo Worthy",
        "Typical Age": "10-15 years",
      },
    },
    {
      id: 5,
      title: "40FT Used Container",
      category: "used",
      image: "/assets/2025/08/40ft-used-container-300x300.jpg",
      description: "Large storage capacity at an affordable price.",
      features: [
        "Inspected for quality",
        "Wind and watertight",
        "Suitable for storage",
        "Great value",
      ],
      fullDescription:
        "These 40ft used containers provide maximum storage space at competitive prices. Each container is thoroughly inspected to ensure it meets our quality standards.",
      specifications: {
        "External Length": "40'",
        "External Width": "8'",
        "External Height": "8.5'",
        "Internal Length": "39'5\"",
        "Internal Width": "7'8\"",
        "Internal Height": "7'10\"",
        Condition: "Cargo Worthy",
        "Typical Age": "10-15 years",
      },
    },
    {
      id: 6,
      title: "20FT Office Container",
      category: "office",
      image: "/assets/2025/08/20ft-office-container-300x300.jpg",
      description: "Fully equipped mobile office solution.",
      features: [
        "Insulated walls & ceiling",
        "HVAC system",
        "Electrical wiring",
        "Windows & doors",
      ],
      fullDescription:
        "Transform your workspace with our fully equipped office containers. These units come with insulation, climate control, electrical systems, and professional finishes.",
      specifications: {
        "External Length": "20'",
        "External Width": "8'",
        "External Height": "8.5'",
        Insulation: "R-13 walls, R-19 ceiling",
        Electrical: "100 amp service",
        HVAC: "Mini-split system",
        Flooring: "Vinyl or carpet",
        Features: "Windows, door, lighting",
      },
    },
    {
      id: 7,
      title: "40FT Office Container",
      category: "office",
      image: "/assets/2025/08/40ft-office-container-300x300.jpg",
      description: "Spacious office with premium amenities.",
      features: [
        "Open floor plan",
        "Built-in restroom option",
        "LED lighting",
        "Professional interior",
      ],
      fullDescription:
        "Our 40ft office containers provide ample space for team collaboration. Customizable layouts can include conference areas, private offices, and restroom facilities.",
      specifications: {
        "External Length": "40'",
        "External Width": "8'",
        "External Height": "8.5'",
        "Floor Area": "320 sq ft",
        Insulation: "R-13 walls, R-19 ceiling",
        Electrical: "200 amp service",
        "Interior Height": "7'10\"",
        Options: "Restroom, kitchenette, partitions",
      },
    },
    {
      id: 8,
      title: "Container Home Module",
      category: "home",
      image: "/assets/2025/08/container-home-300x300.jpg",
      description: "Modern living space with eco-friendly design.",
      features: [
        "Full insulation package",
        "Plumbing ready",
        "Energy efficient",
        "Custom finishes",
      ],
      fullDescription:
        "Create your dream tiny home with our container home modules. Designed for comfortable living with modern amenities and sustainable features.",
      specifications: {
        "Floor Area": "160-320 sq ft",
        Insulation: "Spray foam",
        Windows: "Double-pane vinyl",
        Electrical: "Residential grade",
        Plumbing: "PEX piping",
        Finishes: "Customizable",
      },
    },
    {
      id: 9,
      title: "Custom Modified Container",
      category: "custom",
      image: "/assets/2025/08/custom-container-300x300.jpg",
      description: "Tailored to your specific requirements.",
      features: [
        "Cut-outs & openings",
        "Custom paint colors",
        "Specialized shelving",
        "Any configuration",
      ],
      fullDescription:
        "We can modify containers to meet any specification. From roll-up doors to windows, shelving, lighting, and specialized equipment mounting.",
      specifications: {
        Modifications: "Unlimited options",
        "Cut-outs": "Doors, windows, vents",
        Shelving: "Custom layouts",
        Paint: "Any RAL color",
        "Lead Time": "2-4 weeks",
        Warranty: "1 year",
      },
    },
    {
      id: 10,
      title: "10FT Refrigerated Container",
      category: "custom",
      image: "/assets/2025/08/10ft-reefer-300x300.jpg",
      description: "Temperature-controlled storage solution.",
      features: [
        "Temperature range: -25°C to +25°C",
        "Efficient cooling system",
        "Insulated walls",
        "Digital controls",
      ],
      fullDescription:
        "Perfect for storing temperature-sensitive goods. Our refrigerated containers maintain consistent temperatures and feature reliable cooling systems.",
      specifications: {
        "External Length": "10'",
        "Temperature Range": "-25°C to +25°C",
        "Power Supply": "220V/60Hz",
        Insulation: '4" polyurethane',
        "Cooling Unit": "Carrier or Thermo King",
        "Floor Type": "T-floor",
      },
    },
    {
      id: 11,
      title: "20FT Open Top Container",
      category: "custom",
      image: "/assets/2025/08/20ft-open-top-300x300.jpg",
      description: "Easy top-loading for oversized cargo.",
      features: [
        "Removable tarpaulin roof",
        "Top loading capability",
        "Standard floor",
        "Forklift pockets",
      ],
      fullDescription:
        "Ideal for loading oversized items that won't fit through standard doors. Features a removable roof for crane or forklift access from the top.",
      specifications: {
        "External Length": "20'",
        "Opening Type": "Full roof removal",
        "Roof Material": "Heavy-duty tarpaulin",
        "Door Type": "Standard cargo doors",
        Loading: "Top or rear",
        "Max Load": "62,000 lbs",
      },
    },
    {
      id: 12,
      title: "40FT Flat Rack Container",
      category: "custom",
      image: "/assets/2025/08/40ft-flat-rack-300x300.jpg",
      description: "Open sides for wide or awkward loads.",
      features: [
        "Collapsible sides",
        "Heavy-duty frame",
        "Stake pockets",
        "Versatile loading",
      ],
      fullDescription:
        "Designed for cargo that exceeds standard container dimensions. Collapsible end walls allow for easy loading of machinery and construction materials.",
      specifications: {
        "External Length": "40'",
        "External Width": "8'",
        "Loading Width": "Variable",
        "End Walls": "Collapsible",
        "Side Walls": "None",
        "Max Load": "94,000 lbs",
      },
    },
    {
      id: 13,
      title: "20FT Side Door Container",
      category: "new",
      image: "/assets/2025/08/20ft-side-door-300x300.jpg",
      description: "Convenient side access for easy loading.",
      features: [
        "Full side opening",
        "Standard rear doors",
        "Forklift accessible",
        "Weatherproof seals",
      ],
      fullDescription:
        "Side door containers provide exceptional access for loading and unloading. Perfect for workshops, retail spaces, and storage applications requiring frequent access.",
      specifications: {
        "External Length": "20'",
        "Side Door Width": "7'6\"",
        "Side Door Height": "7'2\"",
        "Rear Doors": "Standard cargo doors",
        Construction: "Corten steel",
        Flooring: "Marine plywood",
      },
    },
    {
      id: 14,
      title: "40FT Double Door Container",
      category: "new",
      image: "/assets/2025/08/40ft-double-door-300x300.jpg",
      description: "Doors on both ends for flow-through access.",
      features: [
        "Doors at both ends",
        "Pass-through capability",
        "Wind and watertight",
        "Tunnel configuration",
      ],
      fullDescription:
        "Double door containers offer maximum flexibility with cargo doors on both ends. Ideal for applications requiring flow-through access or connecting multiple units.",
      specifications: {
        "External Length": "40'",
        "Front Doors": "Full width cargo doors",
        "Rear Doors": "Full width cargo doors",
        Configuration: "Tunnel style",
        "Internal Width": "7'8\"",
        "Internal Height": "7'10\"",
      },
    },
    {
      id: 15,
      title: "Portable Storage Pod",
      category: "home",
      image: "/assets/2025/08/storage-pod-300x300.jpg",
      description: "Compact and mobile storage solution.",
      features: [
        "Weatherproof design",
        "Ground-level access",
        "Lockable door",
        "Portable & stackable",
      ],
      fullDescription:
        "Our portable storage pods are perfect for residential moving, renovation projects, or extra storage. Delivered to your location and picked up when you're ready.",
      specifications: {
        Dimensions: "8' x 5' x 7'",
        Capacity: "280 cu ft",
        "Door Type": "Roll-up or swing",
        Material: "Steel frame, composite panels",
        Features: "Weather-resistant, ventilated",
        "Rental Available": "Yes",
      },
    },
  ];

  // Filter containers based on search query and category
  useEffect(() => {
    let results = allContainers;

    // Filter by category
    if (activeCategory !== "all") {
      results = results.filter(
        (container) => container.category === activeCategory,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter(
        (container) =>
          container.title.toLowerCase().includes(query) ||
          container.description.toLowerCase().includes(query) ||
          container.category.toLowerCase().includes(query) ||
          container.features.some((feature) =>
            feature.toLowerCase().includes(query),
          ),
      );
    }

    setFilteredContainers(results);
  }, [searchQuery, activeCategory]);

  // Update search query from URL params
  useEffect(() => {
    const query = searchParams.get("q");
    if (query) {
      setSearchQuery(query);
    }
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  const handleAddToCart = (container) => {
    addToCart(container);
    setToastMessage(`${container.title} added to cart!`);
    setShowToast(true);
  };

  const handleViewDetails = (id) => {
    navigate(`/containers/${id}`);
  };

  const handleBrowseAll = () => {
    setSearchQuery("");
    setActiveCategory("all");
    setSearchParams({});
  };

  const categories = [
    { id: "all", label: "All Containers" },
    { id: "new", label: "New" },
    { id: "used", label: "Used" },
    { id: "office", label: "Office" },
    { id: "home", label: "Home" },
    { id: "custom", label: "Custom" },
  ];

  return (
    <SearchWrapper>
      {showToast && (
        <Toast
          message={toastMessage}
          type="success"
          onClose={() => setShowToast(false)}
        />
      )}

      <HeroSection>
        <Container>
          <HeroTitle>Search Containers</HeroTitle>
        </Container>
      </HeroSection>

      <SearchSection>
        <Container>
          <SearchBar>
            <input
              type="text"
              placeholder="Search for containers, sizes, features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch(e);
                }
              }}
            />
            <button onClick={handleSearch}>Search</button>
          </SearchBar>

          <FilterTags>
            {categories.map((category) => (
              <FilterTag
                key={category.id}
                active={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.label}
              </FilterTag>
            ))}
          </FilterTags>

          {filteredContainers.length > 0 ? (
            <>
              <ResultsHeader>
                <h2>Search Results</h2>
                <span className="results-count">
                  {filteredContainers.length}{" "}
                  {filteredContainers.length === 1 ? "container" : "containers"}{" "}
                  found
                  {searchQuery && ` for "${searchQuery}"`}
                </span>
              </ResultsHeader>

              <ProductGrid>
                {filteredContainers.map((container) => (
                  <ProductCard key={container.id}>
                    <ProductImage
                      onClick={() => handleViewDetails(container.id)}
                    >
                      <img src={container.image} alt={container.title} />
                    </ProductImage>
                    <ProductContent>
                      <span className="category">{container.category}</span>
                      <h3>{container.title}</h3>
                      <p>{container.description}</p>
                      <ul className="features">
                        {container.features
                          .slice(0, 3)
                          .map((feature, index) => (
                            <li key={index}>
                              <span className="check">✓</span> {feature}
                            </li>
                          ))}
                      </ul>
                      <ButtonGroup>
                        <AddToCartButton
                          inCart={isInCart(container.id)}
                          onClick={() => handleAddToCart(container)}
                        >
                          {isInCart(container.id) ? "In Cart" : "Add to Cart"}
                        </AddToCartButton>
                        <ViewDetailsButton
                          onClick={() => handleViewDetails(container.id)}
                        >
                          View Details
                        </ViewDetailsButton>
                      </ButtonGroup>
                    </ProductContent>
                  </ProductCard>
                ))}
              </ProductGrid>
            </>
          ) : (
            <NoResults>
              <div className="icon">🔍</div>
              <h3>No containers found</h3>
              <p>
                {searchQuery
                  ? `We couldn't find any containers matching "${searchQuery}"`
                  : "Try adjusting your search or browse all containers"}
              </p>
              <SuggestionButton onClick={handleBrowseAll}>
                Browse All Containers
              </SuggestionButton>
            </NoResults>
          )}
        </Container>
      </SearchSection>
    </SearchWrapper>
  );
};

export default Search;
